<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import { VueDraggable } from "vue-draggable-plus";
import { GripVertical, Plus, Trash2 } from "lucide-vue-next";
import { computed, ref, watch } from "vue";
import route from "ziggy-js";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { BookingPage, BookingPageEventType } from "@/types/BookingPage";
import CreateEventTypeModal from "../components/CreateEventTypeModal.vue";

const props = defineProps<{
    eventTypes: BookingPageEventType[];
    bookingPage: BookingPage & { event_types: BookingPageEventType[] };
    canEdit: boolean;
    canCreate: boolean;
    highlightedEventTypeId: number | null;
}>();

const attached = ref(
    [...props.bookingPage.event_types].sort(
        (first, second) =>
            (first.pivot?.order ?? 0) - (second.pivot?.order ?? 0),
    ),
);
const available = ref(
    props.eventTypes.filter(
        (eventType) => !attached.value.some((item) => item.id === eventType.id),
    ),
);
const mutationStatus = ref<"idle" | "saving" | "saved" | "error">("idle");

watch(
    () => [props.bookingPage.event_types, props.eventTypes] as const,
    ([nextAttached, nextEventTypes]) => {
        attached.value = [...nextAttached].sort(
            (first, second) =>
                (first.pivot?.order ?? 0) - (second.pivot?.order ?? 0),
        );
        available.value = nextEventTypes.filter(
            (eventType) =>
                !attached.value.some((item) => item.id === eventType.id),
        );
    },
);

const returnTo = computed(
    () =>
        `${route(
            "app.event-type-groups.edit",
            { eventTypeGroup: props.bookingPage.id },
            false,
        )}#event-types`,
);

function markSaved(): void {
    mutationStatus.value = "saved";
    window.setTimeout(() => {
        if (mutationStatus.value === "saved") mutationStatus.value = "idle";
    }, 2000);
}

function attach(eventType: BookingPageEventType): void {
    if (!props.canEdit) return;

    mutationStatus.value = "saving";
    router.post(
        route("app.event-type-groups.assign-event-type", {
            eventTypeGroup: props.bookingPage.id,
        }),
        { eventTypeId: eventType.id },
        {
            preserveScroll: true,
            onSuccess: markSaved,
            onError: () => {
                mutationStatus.value = "error";
            },
        },
    );
}

function detach(eventType: BookingPageEventType): void {
    if (!props.canEdit) return;

    mutationStatus.value = "saving";
    router.post(
        route("app.event-type-groups.detach-event-type", {
            eventTypeGroup: props.bookingPage.id,
        }),
        { eventTypeId: eventType.id },
        {
            preserveScroll: true,
            onSuccess: markSaved,
            onError: () => {
                mutationStatus.value = "error";
            },
        },
    );
}

function reorder(): void {
    if (!props.canEdit) return;

    mutationStatus.value = "saving";
    router.post(
        route("app.event-type-groups.reorder", {
            eventTypeGroup: props.bookingPage.id,
        }),
        { order: attached.value.map((eventType) => eventType.id) },
        {
            preserveScroll: true,
            onSuccess: markSaved,
            onError: () => {
                mutationStatus.value = "error";
                router.reload({ only: ["bookingPage", "eventTypes"] });
            },
        },
    );
}
</script>

<template>
    <div class="gap-8 flex flex-col">
        <div class="gap-4 flex items-center justify-between">
            <div>
                <p class="text-sm font-semibold text-iron-800">
                    {{ $t("my_page.event_types.attached") }}
                </p>
                <p class="mt-1 text-xs text-iron-500">
                    {{ $t("my_page.event_types.attached_description") }}
                </p>
            </div>
            <p
                class="text-xs font-medium"
                :class="
                    mutationStatus === 'error'
                        ? 'text-error-600'
                        : 'text-iron-400'
                "
                aria-live="polite"
            >
                <span v-if="mutationStatus === 'saving'">{{
                    $t("saving")
                }}</span>
                <span v-else-if="mutationStatus === 'saved'">{{
                    $t("saved")
                }}</span>
                <span v-else-if="mutationStatus === 'error'">{{
                    $t("save_failed")
                }}</span>
                <span v-else>{{
                    $t("my_page.event_types.attached_count", {
                        count: attached.length,
                    })
                }}</span>
            </p>
        </div>

        <VueDraggable
            v-model="attached"
            :animation="150"
            :disabled="!canEdit"
            handle=".event-type-drag-handle"
            ghost-class="opacity-40"
            tag="ul"
            class="gap-3 grid"
            @end="reorder"
        >
            <li
                v-for="eventType in attached"
                :key="eventType.id"
                class="gap-3 rounded-lg border-iron-200 bg-white p-3.5 flex items-center border"
            >
                <button
                    v-if="canEdit"
                    type="button"
                    class="event-type-drag-handle text-iron-300 hover:text-iron-600 cursor-grab"
                    :aria-label="`Reorder ${eventType.name}`"
                >
                    <GripVertical :size="18" />
                </button>
                <div class="min-w-0 flex-1">
                    <div class="gap-2 flex flex-wrap items-center">
                        <p class="text-sm font-semibold text-iron-800 truncate">
                            {{ eventType.name }}
                        </p>
                        <Badge
                            v-if="eventType.disabled"
                            variant="outline"
                            class="border-orange-200 bg-orange-50 text-orange-700"
                        >
                            {{ $t("my_page.event_types.paused_hidden") }}
                        </Badge>
                        <Badge v-else variant="success">{{
                            $t("my_page.state.active")
                        }}</Badge>
                    </div>
                    <p
                        v-if="eventType.duration"
                        class="mt-1 text-xs text-iron-400"
                    >
                        {{ eventType.duration }} min
                    </p>
                </div>
                <Button
                    v-if="canEdit"
                    variant="ghost"
                    size="icon"
                    :aria-label="`Remove ${eventType.name} from page`"
                    @click="detach(eventType)"
                >
                    <Trash2 :size="16" />
                </Button>
            </li>
        </VueDraggable>

        <div
            v-if="attached.length === 0"
            class="rounded-lg border-iron-300 bg-iron-50 px-5 py-8 border border-dashed text-center"
        >
            <p class="text-sm font-medium text-iron-700">
                {{ $t("my_page.event_types.none_attached") }}
            </p>
            <p class="mt-1 text-xs text-iron-500">
                {{ $t("my_page.event_types.none_attached_description") }}
            </p>
        </div>

        <div class="border-iron-100 pt-8 border-t">
            <div
                class="gap-4 sm:flex-row sm:items-center sm:justify-between flex flex-col"
            >
                <div>
                    <p class="text-sm font-semibold text-iron-800">
                        {{ $t("my_page.event_types.available") }}
                    </p>
                    <p class="mt-1 text-xs text-iron-500">
                        <template v-if="bookingPage.team.personal_team">
                            {{
                                $t(
                                    "my_page.event_types.available_description_personal",
                                )
                            }}
                        </template>
                        <template v-else>
                            {{
                                $t(
                                    "my_page.event_types.available_description",
                                    { team: bookingPage.team.name },
                                )
                            }}
                        </template>
                    </p>
                </div>
                <CreateEventTypeModal
                    v-if="canEdit && canCreate"
                    :personal-event-types="[]"
                    :fixed-team="bookingPage.team"
                    :return-to="returnTo"
                >
                    <Button
                        variant="outline"
                        class="gap-2 sm:self-auto self-start whitespace-nowrap"
                    >
                        <Plus :size="16" />
                        {{ $t("my_page.event_types.create") }}
                    </Button>
                </CreateEventTypeModal>
            </div>

            <ul v-if="available.length" class="mt-4 gap-3 sm:grid-cols-2 grid">
                <li
                    v-for="eventType in available"
                    :key="eventType.id"
                    class="gap-4 rounded-lg p-4 flex flex-col justify-between border"
                    :class="
                        eventType.id === highlightedEventTypeId
                            ? 'border-z-primary-300 bg-z-primary-50 ring-z-primary-100 ring-2'
                            : 'border-iron-200'
                    "
                >
                    <div>
                        <div class="gap-2 flex flex-wrap items-center">
                            <p class="text-sm font-semibold text-iron-800">
                                {{ eventType.name }}
                            </p>
                            <Badge
                                v-if="eventType.id === highlightedEventTypeId"
                                variant="outline"
                                class="border-z-primary-200 bg-white text-z-primary-700"
                            >
                                {{ $t("my_page.event_types.just_created") }}
                            </Badge>
                            <Badge
                                v-else-if="eventType.disabled"
                                variant="outline"
                                class="border-orange-200 bg-orange-50 text-orange-700"
                            >
                                {{ $t("my_page.event_types.paused") }}
                            </Badge>
                        </div>
                        <p
                            v-if="eventType.duration"
                            class="mt-1 text-xs text-iron-400"
                        >
                            {{ eventType.duration }} min
                        </p>
                    </div>
                    <Button
                        v-if="canEdit"
                        variant="outline"
                        class="gap-2 self-start"
                        @click="attach(eventType)"
                    >
                        <Plus :size="15" />
                        {{ $t("my_page.event_types.add_to_page") }}
                    </Button>
                </li>
            </ul>
            <p
                v-else
                class="mt-4 rounded-lg bg-iron-50 px-4 py-6 text-sm text-iron-500"
            >
                {{ $t("my_page.event_types.all_attached") }}
            </p>
        </div>
    </div>
</template>
